// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel, { SwiperStyle } from "@steiner/App/Components/XngCarousel";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const getCategoriesParameter = {
        maxDepth: 1,
        includeStartCategory: false,
    };

    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
    };
};

const mapDispatchToProps = {
    loadCategories,
};

interface AllCategoriesStyles {
    carouselSlidesContainer?: InjectableCss;
    categoryCarousel?: SwiperStyle;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    categoryItem?: InjectableCss;
    innerContainer?: InjectableCss;
    categoryImageItem?: InjectableCss;
    categoryImageClickable?: ClickablePresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: InjectableCss;
    categoryNameLink?: LinkPresentationProps;
}

export const allCategoriesStyles: AllCategoriesStyles = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = allCategoriesStyles;
const AllCategories: React.FC<Props> = ({ categoriesDataView, getCategoriesParameter, loadCategories }) => {
    useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }, []);

    if (!categoriesDataView.value || categoriesDataView.value.length === 0) {
        return null;
    }

    if (categoriesDataView.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper data-test-selector="AllCategories">
            <StyledWrapper data-test-selector="slides" {...styles.carouselSlidesContainer}>
                <XngCarousel
                    scrollbar={{ draggable: true, el: ".swiper-scrollbar" }}
                    navigation={true}
                    spaceBetween={28}
                    shadowNavigation={true}
                    loop={false}
                    /* display carousel as two rows  */
                    grid={{ rows: 2, fill: "row" }}
                    /* end of customization  */
                    extendedStyles={styles.categoryCarousel}
                    className="xng-swiper-with-shadow"
                >
                    {categoriesDataView.value.map(category => (
                        <StyledWrapper key={category.id.toString()} {...styles.categoryItem}>
                            <StyledWrapper {...styles.innerContainer}>
                                <StyledWrapper {...styles.categoryImageItem}>
                                    {category.path && (
                                        <Clickable href={category.path} {...styles.categoryImageClickable}>
                                            <LazyImage
                                                src={category.smallImagePath}
                                                {...styles.categoryImage}
                                                altText={category.imageAltText}
                                            />
                                        </Clickable>
                                    )}
                                </StyledWrapper>

                                <StyledWrapper {...styles.categoryNameLinkItem}>
                                    <Link href={category.path} {...styles.categoryNameLink}>
                                        {category.shortDescription}
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </XngCarousel>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AllCategories),
    definition: {
        group: XNGAGE_ELEMENTS,
        allowedContexts: ["HomePage"],
    },
};

export default widgetModule;
